import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getChannels, type Channel, type ChannelFilter } from '@/api/channels'
import { setUserContext, track } from '@/lib/analytics'
import { useShowcaseAccess } from '@/showcase/access'

interface ChannelContextValue {
  channels: Channel[]
  selectedChannel: Channel | null
  selectChannel: (uuid: string) => void
  isLoading: boolean
  channelFilter: ChannelFilter
  setChannelFilter: (f: ChannelFilter) => void
}

const STORAGE_KEY = 'tgx-selected-channel-uuid'
const FILTER_STORAGE_KEY = 'tgx-channel-filter'
// Одноразовый маркер целевого канала после magic-link (AuthContext.tsx).
// Приоритетнее чем STORAGE_KEY — переопределяет любой ранее выбранный канал
// и очищается после применения (single-use).
const MAGIC_TARGET_KEY = 'tgx-magic-target-channel'

const ChannelContext = createContext<ChannelContextValue | null>(null)

export function ChannelProvider({ children }: { children: ReactNode }) {
  // Фильтр 'showcase' — актуально только для superadmin (у обычного юзера
  // фильтр не отличит его собственные каналы от «всех», UI-toggle не даётся).
  // Хранится в localStorage чтобы Виктор при рефреше не терял выбранный режим.
  //
  // CRITICAL: localStorage shared per-domain. Если superadmin поставил
  // 'showcase', потом обычный юзер зашёл в тот же браузер (напр. по magic-link
  // на shared/test-устройстве), localStorage тянул 'showcase' → getChannels
  // возвращал пустой список → «Канал не найден» на tariff → блокировка оплаты.
  // Fix: не-superadmin ВСЕГДА получает effective 'all', независимо от
  // storage. UI-toggle для них и так недоступен, storage-preference игнорим.
  const isSuperadmin = useShowcaseAccess()
  const [channelFilter, setChannelFilterState] = useState<ChannelFilter>(
    () => (localStorage.getItem(FILTER_STORAGE_KEY) as ChannelFilter) || 'all',
  )
  const setChannelFilter = (f: ChannelFilter) => {
    setChannelFilterState(f)
    localStorage.setItem(FILTER_STORAGE_KEY, f)
  }
  const effectiveFilter: ChannelFilter = isSuperadmin ? channelFilter : 'all'

  // Detect + cleanup: не-superadmin имеет 'showcase' в storage = утечка от
  // прошлого superadmin-юзера на этом браузере. Логируем warn (сколько раз
  // происходило) + очищаем storage чтобы future никогда не тянуло.
  useEffect(() => {
    if (isSuperadmin) return
    const leaked = localStorage.getItem(FILTER_STORAGE_KEY)
    if (leaked === 'showcase') {
      track('channel_filter_leaked', {
        level: 'warn',
        leakedValue: leaked,
        message: `channelFilter='${leaked}' в localStorage у не-superadmin — очищено`,
      })
      try {
        localStorage.removeItem(FILTER_STORAGE_KEY)
      } catch {}
    }
  }, [isSuperadmin])

  const { data, isLoading } = useQuery({
    queryKey: ['channels', effectiveFilter],
    queryFn: () => getChannels({ filter: effectiveFilter }),
    staleTime: 60_000,
  })

  const channels = data?.channels ?? []

  // Логируем каждый fetch — сколько каналов пришло, какой filter применён.
  // Дедуп по (count, filter) чтобы не спамить каждый rerender того же state.
  const lastLogRef = useRef<string>('')
  useEffect(() => {
    if (isLoading) return
    const signature = `${channels.length}|${effectiveFilter}`
    if (signature === lastLogRef.current) return
    lastLogRef.current = signature

    if (channels.length === 0) {
      // Пустой список — подозрительно. Обычный юзер должен иметь ≥1 канал
      // (без канала он и до этой страницы дойти не должен). Пометим error.
      track('channels_empty', {
        level: 'error',
        effectiveFilter,
        isSuperadmin,
        storageFilter: localStorage.getItem(FILTER_STORAGE_KEY),
        message: `getChannels вернул 0 каналов · filter=${effectiveFilter}`,
      })
    } else {
      track('channels_loaded', {
        level: 'info',
        channelsCount: channels.length,
        effectiveFilter,
        isSuperadmin,
        message: `каналы загружены · ${channels.length} шт · filter=${effectiveFilter}`,
      })
    }
  }, [channels.length, effectiveFilter, isLoading, isSuperadmin])
  const [selectedUuid, setSelectedUuid] = useState<string>(() => {
    // Приоритет: magic-link target > обычный persist.
    // AuthContext после magic-login пишет MAGIC_TARGET_KEY одноразово —
    // именно этот канал должен быть активен, что бы там ни было в STORAGE_KEY.
    try {
      const magicTarget = localStorage.getItem(MAGIC_TARGET_KEY)
      if (magicTarget) return magicTarget
    } catch {}
    return localStorage.getItem(STORAGE_KEY) || ''
  })

  // Авто-выбор канала после загрузки списка.
  // Три ветки:
  //   1. Есть magic-target в storage → force set (и очистить key single-use)
  //   2. selectedUuid есть в channels → OK, ничего не делаем
  //   3. selectedUuid отсутствует / не в списке → fallback на channels[0]
  useEffect(() => {
    if (channels.length === 0) return
    let magicTarget: string | null = null
    try {
      magicTarget = localStorage.getItem(MAGIC_TARGET_KEY)
    } catch {}
    if (magicTarget) {
      const magicChannel = channels.find((c) => c.uuid === magicTarget)
      if (magicChannel) {
        // Форсим — даже если selectedUuid уже был установлен на другой канал
        // race-condition'ом с pre-magic-link состоянием.
        if (selectedUuid !== magicTarget) {
          setSelectedUuid(magicTarget)
          try {
            localStorage.setItem(STORAGE_KEY, magicTarget)
          } catch {}
        }
        // Single-use: очищаем чтобы юзер мог свободно переключаться дальше
        try {
          localStorage.removeItem(MAGIC_TARGET_KEY)
        } catch {}
        return
      }
      // magic-target есть, но канал не в списке (может ещё не подгрузился
      // или бэк отфильтровал) — оставляем key на следующий tick.
      return
    }
    const exists = channels.find((c) => c.uuid === selectedUuid)
    if (!exists) {
      const first = channels[0]
      setSelectedUuid(first.uuid)
      localStorage.setItem(STORAGE_KEY, first.uuid)
    }
  }, [channels, selectedUuid])

  const selectChannel = (uuid: string) => {
    setSelectedUuid(uuid)
    localStorage.setItem(STORAGE_KEY, uuid)
  }

  const selectedChannel = channels.find((c) => c.uuid === selectedUuid) || null

  // Прокидываем (maxChannelID, channelTitle) в analytics — будут добавлены ко
  // всем последующим track() событиям. Без этого в логах был только channelUUID,
  // что неудобно для джоина с бэкенд-логами (там MAX-айди в формате -74946...).
  useEffect(() => {
    setUserContext({
      maxChannelID: selectedChannel?.maxChannelID,
      channelTitle: selectedChannel?.title,
    })
  }, [selectedChannel?.maxChannelID, selectedChannel?.title])

  return (
    <ChannelContext.Provider value={{ channels, selectedChannel, selectChannel, isLoading, channelFilter, setChannelFilter }}>
      {children}
    </ChannelContext.Provider>
  )
}

export function useChannel() {
  const ctx = useContext(ChannelContext)
  if (!ctx) throw new Error('useChannel must be used inside ChannelProvider')
  return ctx
}
